import React from 'react';
import { AppAlert, AppButton, AppFormField, AppFormLabel, AppInput, AppModal, AppSelect, AppStatusBadge } from '../../ui';
import { ADMIN_FIELD_HELP } from '../../../utils/adminHelpContent';

export const ADMIN_STATUS_BADGE = {
  active: { className: 'bg-success-100 text-success-600', label: 'Actif' },
  scheduled: { className: 'bg-info-100 text-info-600', label: 'Programmé' },
  expired: { className: 'bg-neutral-200 text-secondary-light', label: 'Expiré' },
  inactive: { className: 'bg-danger-100 text-danger-600', label: 'Inactif' },
};

export const ADMIN_WEEKDAYS = [
  { value: 1, label: 'Lundi' },
  { value: 2, label: 'Mardi' },
  { value: 3, label: 'Mercredi' },
  { value: 4, label: 'Jeudi' },
  { value: 5, label: 'Vendredi' },
  { value: 6, label: 'Samedi' },
  { value: 7, label: 'Dimanche' },
];

export const defaultAdminPeriod = () => {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
};

const VALIDITY_STATUS_MAP = {
  active: { status: 'active', label: 'Actif' },
  scheduled: { status: 'scheduled', label: 'Programmé' },
  expired: { status: 'expired', label: 'Expiré' },
  inactive: { status: 'invalid', label: 'Inactif' },
};

export const AdminValidityBadge = ({ status }) => {
  const cfg = VALIDITY_STATUS_MAP[status] ?? VALIDITY_STATUS_MAP.inactive;
  return <AppStatusBadge status={cfg.status} label={cfg.label} />;
};

const ACTION_CLASS_TO_VARIANT = {
  'btn-outline-success': 'outline-success',
  'btn-outline-warning': 'outline-warning',
  'btn-outline-danger': 'outline-danger',
  'btn-outline-secondary': 'outline-secondary',
  'btn-outline-primary': 'outline-primary',
};

export const AdminPeriodFields = ({
  year,
  month,
  moisOptions,
  onYearChange,
  onMonthChange,
  yearColClass = 'col-md-6',
  monthColClass = 'col-md-6',
}) => (
  <>
    <div className={yearColClass}>
      <AppFormLabel help={ADMIN_FIELD_HELP.validFromYear.help} helpExample={ADMIN_FIELD_HELP.validFromYear.helpExample}>
        Actif depuis — année
      </AppFormLabel>
      <AppInput
        type="number"
        required
        value={year}
        onChange={(e) => onYearChange(Number(e.target.value))}
      />
    </div>
    <div className={monthColClass}>
      <AppFormLabel help={ADMIN_FIELD_HELP.validFromMonth.help} helpExample={ADMIN_FIELD_HELP.validFromMonth.helpExample}>
        Mois
      </AppFormLabel>
      <AppSelect value={month} onChange={(e) => onMonthChange(Number(e.target.value))}>
        {moisOptions.map((m) => (
          <option key={m.value} value={m.value}>{m.label}</option>
        ))}
      </AppSelect>
    </div>
  </>
);

export const AdminDeactivateModal = ({
  target,
  period,
  moisOptions,
  saving,
  onClose,
  onConfirm,
  onPeriodChange,
}) => (
  <AppModal
    show={Boolean(target)}
    title="Désactiver à partir de…"
    onClose={onClose}
    footer={(
      <>
        <AppButton variant="outline-secondary" onClick={onClose}>Annuler</AppButton>
        <AppButton variant="warning" loading={saving} loadingLabel="Enregistrement…" onClick={onConfirm}>
          Désactiver
        </AppButton>
      </>
    )}
  >
    <p className="text-sm text-secondary-light">
      <strong>{target?.label ?? target?.value}</strong>
      {' '}ne sera plus proposé à partir du mois choisi. L&apos;historique est conservé.
    </p>
    <div className="row g-3">
      <AppFormField className="col-6" label="Année">
        <AppInput
          type="number"
          value={period.year}
          onChange={(e) => onPeriodChange({ ...period, year: Number(e.target.value) })}
        />
      </AppFormField>
      <AppFormField className="col-6" label="Mois (premier mois inactif)">
        <AppSelect
          value={period.month}
          onChange={(e) => onPeriodChange({ ...period, month: Number(e.target.value) })}
        >
          {moisOptions.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </AppSelect>
      </AppFormField>
    </div>
  </AppModal>
);

export const AdminActionButton = ({
  title,
  icon,
  className = 'btn-outline-secondary',
  variant,
  disabled = false,
  onClick,
  showLabel = false,
}) => (
  <AppButton
    size="sm"
    variant={variant || ACTION_CLASS_TO_VARIANT[className] || 'outline-secondary'}
    icon={icon}
    className="d-inline-flex align-items-center gap-1"
    title={title}
    aria-label={title}
    disabled={disabled}
    onClick={onClick}
  >
    {showLabel ? <span className="admin-action-btn__label">{title}</span> : null}
  </AppButton>
);

export const AdminValidityActions = ({ item, saving, onDeactivate, onReactivate, onDelete }) => (
  <div className="d-flex flex-wrap gap-1 justify-content-end">
    {item.validity_status === 'expired' || item.validity_status === 'inactive' || item.valid_until_year ? (
      <AdminActionButton
        title="Réactiver"
        icon="mdi:restore"
        className="btn-outline-success"
        disabled={saving}
        onClick={() => onReactivate(item.id)}
      />
    ) : (
      <AdminActionButton
        title="Désactiver"
        icon="mdi:pause-circle-outline"
        className="btn-outline-warning"
        disabled={saving}
        onClick={() => onDeactivate(item)}
      />
    )}
    {onDelete && (
      <AdminActionButton
        title="Supprimer"
        icon="mdi:delete-outline"
        className="btn-outline-danger"
        disabled={saving}
        onClick={() => onDelete(item)}
      />
    )}
  </div>
);

export const ADMIN_DELETE_MESSAGES = {
  'mag-format': {
    title: 'Supprimer ce format MAG',
    consequences: [
      'Le format disparaît des listes de saisie et de l’administration.',
      'Les ventes MAG déjà enregistrées avec ce format restent en base avec leur libellé actuel.',
      'Vous ne pourrez plus sélectionner ce format pour de nouvelles ventes.',
      'Action définitive — préférez la désactivation si le format peut être réutilisé plus tard.',
    ],
  },
  'mag-contenu': {
    title: 'Supprimer ce contenu MAG',
    consequences: [
      'Le contenu disparaît des listes de saisie et de l’administration.',
      'Les ventes existantes conservent leur contenu en base.',
      'Action définitive — impossible de restaurer après suppression.',
    ],
  },
  'set-format': {
    title: 'Supprimer ce format SET',
    consequences: [
      'Les lignes vides de la grille (créneaux sans vente) pour ce format ne seront plus générées.',
      'Les ventes SET déjà saisies avec ce format restent en base.',
      'La configuration de période (actif depuis / jusqu’à) sera perdue.',
      'Action définitive — utilisez « Désactiver » pour masquer temporairement sans effacer.',
    ],
  },
  'newsletter-emplacement': {
    title: 'Supprimer cet emplacement newsletter',
    consequences: [
      'Les créneaux vides (mercredi / vendredi) pour cet emplacement ne seront plus affichés dans le planning.',
      'Les ventes newsletter déjà enregistrées sur cet emplacement restent en base.',
      'La configuration de période sera perdue.',
      'Action définitive — la désactivation permet de conserver l’historique de configuration.',
    ],
  },
  'newsletter-type': {
    title: 'Supprimer ce type newsletter',
    consequences: [
      'Les envois planifiés pour ce type (jour d’envoi) ne seront plus générés.',
      'Les emplacements rattachés à ce type doivent être supprimés ou désactivés avant la suppression.',
      'Les ventes existantes avec ce type restent en base.',
      'Action définitive et irréversible.',
    ],
  },
  tarif: {
    title: 'Supprimer ce tarif Plan de Com',
    consequences: [
      'Le tarif disparaît des listes de chiffrage et de l’administration.',
      'Les dossiers déjà chiffrés avec ce libellé conservent leurs montants en base.',
      'Préférez la désactivation par période pour masquer sans effacer.',
    ],
  },
  produit: {
    title: 'Supprimer ce produit BDC',
    consequences: [
      'Le produit disparaît des libellés proposés sur le bon de commande.',
      'Les BDC déjà générés conservent leurs libellés en base.',
      'Préférez la désactivation par période pour masquer sans effacer.',
    ],
  },
  klub: {
    title: 'Supprimer cette offre Klub',
    consequences: [
      'L’offre disparaît de l’administration Klub.',
      'La grille tarifaire Klub ne la proposera plus.',
      'Action définitive — préférez la désactivation par période pour conserver l’historique.',
    ],
  },
  statut: {
    title: 'Supprimer ce statut de phase',
    consequences: [
      'Le statut disparaît des listes MAG, SET et Newsletters.',
      'Les ventes qui utilisent déjà ce statut restent inchangées en base.',
      'Vous ne pourrez plus attribuer ce statut à de nouvelles ventes.',
      'Action définitive — préférez la désactivation par période pour le masquer sans supprimer.',
    ],
  },
};

export const resolveDeleteContext = (item, contextKey = null) => {
  if (contextKey && ADMIN_DELETE_MESSAGES[contextKey]) {
    return ADMIN_DELETE_MESSAGES[contextKey];
  }
  if (item.canal === 'mag' && item.type === 'format') return ADMIN_DELETE_MESSAGES['mag-format'];
  if (item.canal === 'mag' && item.type === 'contenu') return ADMIN_DELETE_MESSAGES['mag-contenu'];
  if (item.canal === 'set' && item.type === 'format') return ADMIN_DELETE_MESSAGES['set-format'];
  if (item.canal === 'newsletter' && item.type === 'format') return ADMIN_DELETE_MESSAGES['newsletter-emplacement'];
  if (item.canal === 'newsletter' && item.type === 'contenu') return ADMIN_DELETE_MESSAGES['newsletter-type'];
  if (contextKey === 'klub') return ADMIN_DELETE_MESSAGES.klub;
  if (item.type === 'statut_phase') return ADMIN_DELETE_MESSAGES.statut;
  return {
    title: 'Supprimer cette valeur',
    consequences: [
      'La valeur sera retirée de l’administration.',
      'Les données historiques déjà saisies peuvent rester en base.',
      'Cette action est définitive.',
    ],
  };
};

export const AdminDeleteModal = ({
  target,
  contextKey,
  saving,
  onClose,
  onConfirm,
}) => {
  if (!target) return null;

  const { title, consequences } = resolveDeleteContext(target, contextKey);

  return (
    <AppModal
      show
      title={title}
      onClose={onClose}
      footer={(
        <>
          <AppButton variant="outline-secondary" onClick={onClose}>Annuler</AppButton>
          <AppButton variant="danger" loading={saving} loadingLabel="Suppression…" onClick={onConfirm}>
            Supprimer définitivement
          </AppButton>
        </>
      )}
    >
      <p className="mb-3">
        Vous allez supprimer définitivement <strong>{target.label ?? target.value ?? target.libelle ?? target.code}</strong>.
      </p>
      <AppAlert variant="warning" className="text-sm py-2 mb-0">
        <strong>Conséquences si vous confirmez :</strong>
        <ul className="mb-0 mt-2 ps-3">
          {consequences.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </AppAlert>
    </AppModal>
  );
};
